import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Wrench } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

const TYPE_ICON = {
  intervention: Wrench,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/notifications`);
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // poll toutes les 30s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleOpenItem = async (n) => {
    if (!n.read) {
      try { await axios.post(`${API}/api/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    setOpen(false);
    await load();
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/read-all`);
      await load();
    } catch (e) { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow"
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="font-semibold text-gray-900">Notifications</div>
            {unread > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={handleMarkAllRead}
                className="text-xs text-blue-700 hover:bg-blue-100"
                data-testid="notif-mark-all-read"
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" /> Tout marquer lu
              </Button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                Aucune notification pour le moment.
              </div>
            ) : (
              items.map(n => {
                const Icon = TYPE_ICON[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleOpenItem(n)}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-blue-50/50 transition-colors ${
                      !n.read ? 'bg-blue-50/30' : ''
                    }`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      n.type === 'intervention' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Icon className={`h-4 w-4 ${
                        n.type === 'intervention' ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></span>}
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
